import React, { useEffect, useState } from "react";
import { Button } from "@material-tailwind/react";
import { styled } from "@mui/material/styles";
import { Box, CircularProgress, Tab, Tabs, Tooltip, IconButton, Divider } from "@mui/material";
import { type PageLayout, Worker, Viewer, SpecialZoomLevel } from "@react-pdf-viewer/core";
import { thumbnailPlugin } from "@react-pdf-viewer/thumbnail";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { addUserForm } from "../../store/thunk/formThunk";
import { notificationFail } from "../../store/slices/notificationSlice";
import SignInSignUpButton from "../payAndDownload/signInSignUpButton";
import ValidateFormJson from "../../components/stepper/validateFormJson";
import { checkDependantQuestion, anonymousUser } from "../../helper/service";
import { generateGuid } from "../../helper/generateGuid";
import NoPdf from "../../assets/images/NoPdf.svg";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/thumbnail/lib/styles/index.css";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(0, 1, 0, 2),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "space-between",
}));

let pdfformselected = "";

export default function PreviewPDF(props: any) {
  const dispatch = useAppDispatch();
  const { togglePreviewDrawer, hidden } = props;

  const currentQuestionList = useAppSelector((state: any) => state.formReducer.currentQuestionList);

  const localCaseTypeData: any = localStorage.getItem("caseTypeDetails");
  const caseTypeDetails: any = JSON.parse(localCaseTypeData);

  const localData: any = localStorage.getItem("bootstrapData");
  const bootstrapData: any = JSON.parse(localData);

  useEffect(() => {
    const matchingFormData = bootstrapData?.formsForCaseType?.find(
      (form: any) => form.caseId === caseTypeDetails.caseId
    );

    // Append formNames if a match is found
    if (matchingFormData) {
      caseTypeDetails.formNames = matchingFormData.formNames;
      caseTypeDetails.isStandalone = matchingFormData.isStandalone;
    }

    // Save the updated caseTypeDetails back to localStorage
    localStorage.setItem("caseTypeDetails", JSON.stringify(caseTypeDetails));
  }, [caseTypeDetails]);

  const [value, setValue] = useState<any>();
  const [formName, setFormName] = useState<string>("");
  const [pdfUrl, setPdfUrl] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const pdfBaseUrl = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

  const pageLayout: PageLayout = {};
  const thumbnailPluginInstance = thumbnailPlugin();
  const pageNavigationPluginInstance = pageNavigationPlugin();

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number, key: string) => {
    setFormName(key || "");
    setValue(newValue);
  };

  const [pdfUrlList, setPDFUrlList] = useState<any>([
    // "i-864",
    // "i-130",
    // "i-130a",
    // "i-131",
    // "i-485",
    // "i-765",
    // "g-1145",
    // "i-693",
  ]);

  useEffect(() => {
    if (caseTypeDetails?.length > 0) {
      setPDFUrlList(caseTypeDetails[0]?.formNames);
    }
  }, []);

  useEffect(() => {
    const formValuesForPDF: any = sessionStorage.getItem("unSavedFormValues");
    const tempFormValues = JSON.parse(formValuesForPDF);
    let updatedList: any = []; // "i-485"

    const tempCaseTypeFormName = [...caseTypeDetails?.formNames];

    // Check if this is the default marriage-based green card workflow (has i-485)
    const isMarriageBasedGreenCard = tempCaseTypeFormName?.some((form: string) =>
      form === "i-485" || form === "i-130"
    );

    if (!caseTypeDetails?.isStandalone && isMarriageBasedGreenCard) {
      // Original hardcoded logic for marriage-based green card workflow
      updatedList.push("i-485");
      if (tempFormValues?.GTKY24 === "N") {
        updatedList.push("i-130", "i-130a");
      }
      if (tempFormValues?.I864Wflag === "N") {
        updatedList.push("i-864");
      }
      if (
        tempFormValues?.GTKY25?.includes("AllYes") ||
        tempFormValues?.GTKY25?.includes("I765Yes")
      ) {
        updatedList.push("i-765");
      }
      if (
        tempFormValues?.GTKY25?.includes("AllYes") ||
        tempFormValues?.GTKY25?.includes("I131Yes")
      ) {
        updatedList.push("i-131");
      }
      if (
        tempFormValues?.GTKY25?.includes("AllYes") ||
        tempFormValues?.GTKY25?.includes("G1145Yes") ||
        anonymousUser()
      ) {
        updatedList.push("g-1145");
      }
      if (tempFormValues?.MED1031 === "N") {
        updatedList.push("i-693");
      }
      if (
        tempFormValues?.I864Wflag === "N" &&
        tempFormValues?.income_limit_flag > 4 &&
        tempFormValues?.income_limit_flag < 7
      ) {
        updatedList.push("i-864a-household-member-1");
      }
      if (tempFormValues?.I864Wflag === "N" && tempFormValues?.income_limit_flag > 6) {
        updatedList.push("i-864-joint-sponsor");
      }
      if (tempFormValues?.I864Wflag === "N" && tempFormValues?.income_limit_flag > 8) {
        updatedList.push("i-864a-household-member-2");
      }
      if (tempFormValues?.Q1Headers === "Y") {
        updatedList.push("g-28-immigrant", "g-28-petitioner");
      }
      if (tempFormValues?.I864Wflag === "Y") {
        updatedList = updatedList.filter((item: string) => item !== "i-864");
      }

      updatedList = Array.from(new Set(updatedList));

      setPDFUrlList([...updatedList]);
    } else {
      // For standalone cases OR custom case types, use formNames from bootstrap
      setPDFUrlList(tempCaseTypeFormName);
    }
    if (updatedList.length > value) {
      setFormName(updatedList[value]);
    } else if (tempCaseTypeFormName.length > value) {
      setFormName(tempCaseTypeFormName[value]);
    } else {
      setFormName("");
      setPdfUrl("");
      setValue(updatedList.length);
    }

    if (anonymousUser()) {
      const indexx = updatedList.findIndex((val: any) => val === "g-1145");
      setFormName("g-1145");
      setValue(indexx);
    }
  }, [sessionStorage.getItem("unSavedFormValues")]);

  const loadData = () => {
    const baseUrl = window.API_URL;
    const usapathEmulateUserId = window.sessionStorage.getItem("Usapath-Emulate-UserId");
    // **** if requirement is to use different session ID for all the tabs  ****
    let sessionID = sessionStorage.getItem("Usapath-Session-Id");
    if (!sessionID) {
      sessionID = generateGuid();
      sessionStorage.setItem("Usapath-Session-Id", sessionID);
    }

    const headers: any = {};
    if (usapathEmulateUserId) {
      headers["Usapath-Emulate-UserId"] = usapathEmulateUserId;
    }

    // AnonymousUser can access form G-1145 only
    headers["Usapath-Request-Id"] = generateGuid();
    headers["Usapath-Session-Id"] = sessionID;
    if (
      // windowWidth > 767 &&
      !anonymousUser() ||
      (anonymousUser() && formName === "g-1145")
    ) {
      setLoading(true);
      const formData: any = sessionStorage.getItem("unSavedFormValues") || "{}";

      const requestOptions = {
        method: anonymousUser() ? "POST" : "GET",
        headers: {
          ...headers,
          ...(anonymousUser()
            ? { "Content-Type": "application/json" }
            : {
                Authorization: `Bearer ${window.localStorage.getItem("accessToken")}`,
              }), // Include headers only if not anonymous
        },
        body: anonymousUser() ? formData : null,
      };

      fetch(
        `${baseUrl}/PdfPreview/${
          anonymousUser() && formName === "g-1145" ? "g1145" : formName
        }?caseId=${caseTypeDetails.caseId}`,
        requestOptions
      )
        .then((response) => response.blob())
        .then((blob) => {
          if (pdfformselected === formName) {
            // Create a URL for the blob data
            const pdfBlob = new Blob([blob], {
              type: "application/pdf", // Old Code: type: "application/pdf" || "application/x-pdf"
            });
            const url = URL.createObjectURL(pdfBlob);
            setPdfUrl(`${url}#toolbar=0&navpanes=0`);
          }
        })
        .catch(() => {
          dispatch(
            notificationFail("We have detected an error. Please contact support for assistance.")
          );
        })
        .finally(() => setLoading(false));
    } else {
      setPdfUrl("");
    }
  };

  const debouncedLoadData = (refresh = false) => {
    pdfformselected = formName;
    if (pdfformselected === formName && pdfformselected !== "" && formName !== "") {
      if (refresh) {
        loadData();
      } else {
        setTimeout(() => {
          loadData();
        }, 500);
      }
    }
  };

  useEffect(() => {
    debouncedLoadData();
  }, [formName]);

  const refreshData = () => {
    saveUpdatedField();
  };

  const saveUpdatedField = () => {
    const queList = [...currentQuestionList];
    const unSavedFormValues = JSON.parse(sessionStorage.getItem("unSavedFormValues") || "{}");
    const updatedFormValues = JSON.parse(sessionStorage.getItem("updatedFormValues") || "{}");

    const list = queList.filter((val: any) => checkDependantQuestion(val, unSavedFormValues));
    const err = ValidateFormJson({ formData: unSavedFormValues, list });

    const filteredObj2 = Object.keys(updatedFormValues).reduce((acc: any, key: any) => {
      if (!err.hasOwnProperty(key)) {
        acc[key] = updatedFormValues[key];
      }
      return acc;
    }, {});
    dispatch(
      addUserForm({
        form_id: 1073,
        form_data: JSON.stringify(filteredObj2),
      })
    )
      .then(() => {
        debouncedLoadData(true);
      })
      .catch(() => {
        debouncedLoadData(true);
      });
  };

  const tabColors = [
    "#d0fffe",
    "#fffddb",
    "#e4ffde",
    "#ffd3fd",
    "#ffe7d3",
    "#d0fffe",
    "#fffddb",
    "#e4ffde",
    "#d0fffe",
    "#fffddb",
    "#e4ffde",
    "#ffe7d3",
    "#d0fffe",
    "#fffddb",
    "#e4ffde",
    "#d0fffe",
    "#fffddb",
    "#e4ffde",
  ];

  const formLabels: { [key: string]: string } = {
    "i-864a-household-member-1": "I864a Household Member of Petitioner",
    "i-864a-household-member-2": "I864a Household Member of Joint Sponsor",
    "i-864-joint-sponsor": "I864 for Joint Sponsor",
    "g-28-immigrant": "G28 for Immigrant",
    "g-28-petitioner": "G28 for Petitioner",
  };

  return (
    <Box className="form_preview_container" sx={{ height: !hidden ? "calc(100% - 30px)" : "100%" }}>
      <DrawerHeader
        className="drawer-header drawerHeaderPreview"
        sx={{ justifyContent: "center", p: "0 0" }}
      >
        <div
          className="form_preview_inner_container w-full flex justify-between py-2"
          style={{ padding: "8px" }}
        >
          <span className="Sen-Bold self-center text-md font-extrabold whitespace-nowrap light:text-white text-[#0C1932]">
            Form Preview
          </span>
          <div style={{ display: "flex" }}>
            <Tooltip arrow title="Refresh">
              <Button
                className="refresh_pdf_btn"
                color="blue"
                onClick={refreshData}
                placeholder="Test"
                size="sm"
                type="button"
                variant="outlined"
              >
                <RefreshIcon sx={{ color: "black", fontSize: "14px", marginRight: "2px" }} />
                Refresh
              </Button>
            </Tooltip>
            <Tooltip arrow title="close">
              <IconButton className="close_preview_btn" onClick={togglePreviewDrawer}>
                <CloseIcon sx={{ color: "black", fontSize: "18px" }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <Box
          className="preview_pdf_container"
          sx={{
            width: "100%",
            bgcolor: "background.paper",
            flexGrow: 1,
            maxWidth: { xs: 320, sm: 480 },
          }}
        >
          <Tabs
            aria-label="scrollable auto tabs example"
            className="tabs_container"
            onChange={(event, newValue) => handleChange(event, newValue, "")}
            scrollButtons="auto"
            value={value}
            variant="scrollable"
          >
            {pdfUrlList &&
              pdfUrlList?.map((obj: any, ind: any) => (
                <Tab
                  className="pdf_tab_link"
                  id={obj}
                  key={obj}
                  label={formLabels[obj] || obj}
                  onClick={(event) => handleChange(event, ind, obj)}
                  sx={{
                    color: "black",
                    padding: "0px",
                    backgroundColor: tabColors[ind],
                  }}
                />
              ))}
          </Tabs>
        </Box>
      </DrawerHeader>
      <Divider />
      {loading ? (
        <Box
          className="pdf_view_loader"
          sx={{
            height: "calc(100% - 100px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {windowWidth < 767 && anonymousUser() ? (
            <div
              className="signin_up_btn_container"
              style={{
                height: "calc(100% - 70px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SignInSignUpButton />
            </div>
          ) : (
            <>
              {anonymousUser() && formName !== "g-1145" ? (
                <div
                  className="signin_up_btn_container"
                  style={{
                    height: "calc(100% - 100px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SignInSignUpButton />
                </div>
              ) : (
                <>
                  {pdfUrl ? (
                    <div
                      className="pdf_view_container"
                      style={{
                        height: "calc(100% - 100px)",
                      }}
                    >
                      <Worker workerUrl={pdfBaseUrl}>
                        <Viewer
                          defaultScale={SpecialZoomLevel.PageWidth}
                          fileUrl={pdfUrl}
                          httpHeaders={{
                            mode: "no-cors",
                          }}
                          initialPage={0}
                          pageLayout={pageLayout}
                          plugins={[pageNavigationPluginInstance, thumbnailPluginInstance]}
                        />
                      </Worker>
                    </div>
                  ) : (
                    <div className="noPdfText">
                      <img alt="noPdfText" src={NoPdf} />
                      <p>Please select one of the forms above to see the preview</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
